import { selectLoyaltyRedemption } from "../../services/redux/selectors/loyaltyCartSelectors";
import {
  OrderCartV2AppliedOffer,
  OrderCartV2Line,
  OrderCartV2OfferSelection,
} from "../../services/redux/types/OrderCartV2Types";
import { hasBuyRequirements, isBundleShaped } from "../loyalty/offerShape";
import {
  LoyaltyApplicationLevel,
  LoyaltyOfferType,
  LoyaltyTargetType,
} from "../loyaltyTypes";
import { CreateReceiptAppliedOffer } from "../types/createReceipt";

type LoyaltyRedemption = ReturnType<typeof selectLoyaltyRedemption>;

/**
 * Shared offer-selection resolution used by BOTH the loyaltyV2 cart-calculate
 * flow and the loyaltyV2 create-receipt flow, so they stay in sync.
 *
 * `buildAppliedOffers` returns the calculate-shaped array (with `selectionMode`);
 * `buildCreateReceiptAppliedOffer` derives the create-receipt single-object shape
 * from the same selections.
 */

function buildSelectionsFromOfferConfig(
  offer: LoyaltyOfferType,
  cartLines: OrderCartV2Line[]
): OrderCartV2OfferSelection[] {
  const config = offer.itemLevelConfig;
  if (!config?.targetEntities?.length) {
    return [];
  }
  if (config.targetType !== LoyaltyTargetType.ITEMS) {
    return [];
  }

  const selections: OrderCartV2OfferSelection[] = [];

  for (const entity of config.targetEntities) {
    const quantity = entity.maximumQuantity ?? entity.minimumQuantity ?? 1;
    const matchingLine = cartLines.find(
      (line) => line.subItemId === entity.entityId && line.lineId
    );
    if (!matchingLine?.lineId) {
      continue;
    }
    selections.push({ lineId: matchingLine.lineId, quantity });
  }

  return selections;
}

function buildExplicitSelectionsFromRedemption(
  redemption: NonNullable<LoyaltyRedemption>,
  cartLines: OrderCartV2Line[]
): OrderCartV2OfferSelection[] {
  if (redemption.pendingSelections?.length) {
    return redemption.pendingSelections;
  }

  if (redemption.addedLineIds?.length) {
    const lineById = new Map(
      cartLines
        .filter((line) => line.lineId)
        .map((line) => [line.lineId!, line])
    );

    return redemption.addedLineIds
      .filter((lineId) => lineById.has(lineId))
      .map((lineId) => ({
        lineId,
        quantity: lineById.get(lineId)!.quantity,
      }));
  }

  return buildSelectionsFromOfferConfig(redemption.offer, cartLines);
}

export function buildAppliedOffers(
  redemption: LoyaltyRedemption,
  cartLines: OrderCartV2Line[]
): OrderCartV2AppliedOffer[] | undefined {
  const redeemedLoyaltyOffer = redemption?.offer;
  const redeemedOfferApplicationId = redemption?.offerApplicationId;

  if (!redeemedLoyaltyOffer || !redeemedOfferApplicationId || !redemption) {
    return undefined;
  }

  const offerApplicationId = redeemedOfferApplicationId;
  const offerId = redeemedLoyaltyOffer.id;
  const isItemLevel =
    redeemedLoyaltyOffer.applicationLevel === LoyaltyApplicationLevel.ITEM;
  const isBogo = hasBuyRequirements(redeemedLoyaltyOffer);
  const isBundle = isBundleShaped(redeemedLoyaltyOffer.itemLevelConfig);
  const isCategoryTarget =
    redeemedLoyaltyOffer.itemLevelConfig?.targetType ===
    LoyaltyTargetType.CATEGORIES;

  /*
   * Guest-built offers: `selections` is the reward side, `triggers` the buy
   * side. Either alone is enough to send — a bundle has only selections, and
   * a BOGO paying out on the cart has only triggers, since its reward is not
   * a set of items the guest picks. Gating this on ITEM level sent the latter
   * as AUTO, which threw away the very lines the builder had just pushed and
   * left the server to re-pick the buy side from the whole cart.
   *
   * Without a client-built selection these fall through to AUTO below.
   */
  if ((isBogo || isBundle) && !isCategoryTarget) {
    const selections = redemption.pendingSelections ?? [];
    const triggers = redemption.pendingTriggers ?? [];

    if (selections.length || triggers.length) {
      return [
        {
          offerApplicationId,
          offerId,
          selectionMode: "EXPLICIT",
          ...(selections.length && { selections }),
          ...(triggers.length && { triggers }),
        },
      ];
    }
  }

  if (!isItemLevel || isBogo || isBundle || isCategoryTarget) {
    return [{ offerApplicationId, offerId, selectionMode: "AUTO" }];
  }

  const hasClientSelections =
    (redemption.pendingSelections?.length ?? 0) > 0 ||
    (redemption.addedLineIds?.length ?? 0) > 0;

  if (!hasClientSelections) {
    return undefined;
  }

  const selections = buildExplicitSelectionsFromRedemption(
    redemption,
    cartLines
  );

  if (!selections.length) {
    return undefined;
  }

  return [
    { offerApplicationId, offerId, selectionMode: "EXPLICIT", selections },
  ];
}

/**
 * Derives the create-receipt single-object applied-offer shape
 * (`{ offerId, offerApplicationId, selections?, triggers? }`) from the same
 * selection logic the cart-calculate flow uses — so both stay in sync.
 */
export function buildCreateReceiptAppliedOffer(
  redemption: LoyaltyRedemption,
  cartLines: OrderCartV2Line[]
): CreateReceiptAppliedOffer | undefined {
  const applied = buildAppliedOffers(redemption, cartLines);
  if (!applied?.length) {
    return undefined;
  }

  const { offerId, offerApplicationId, selections, triggers } = applied[0];

  return { offerId, offerApplicationId, selections, triggers };
}
